import { LAB_CLOUD_URL } from './constants'
import {
  environmentConfigs,
  isFirebaseProjectID,
  type TEnvironmentName,
} from '@team-milva/lab-constants/environments'

const emulators = import.meta.env.VITE_APP_EMULATORS
const isEmulatingFunctions = emulators && emulators.includes('functions')
const isEmulatingFirestore = emulators && emulators.includes('firestore')
const isEmulatingStorage = emulators && emulators.includes('storage')
const isEmulatingPubsub = emulators && emulators.includes('pubsub')
const isLocalhost = ['localhost', '127.0.0.1'].includes(
  window.location.hostname
)

// ENV and HOST are mutable — populated once by initEnvironment() during
// bootstrap, after the Firebase project ID has been resolved at runtime.
export const ENV = {
  isProd: false,
  isDev: true,
  isLocalhost,
  /** Empty until initEnvironment() has run. */
  projectID: '',
  /**
   * Short name of the environment this build is running against — 'dev',
   * 'stage' or 'prod', from the shared environment table. Empty until
   * initEnvironment() has run. Prefer this over isProd/isDev anywhere the
   * environment has to be named rather than merely tested.
   */
  environmentName: '' as TEnvironmentName | '',
}

/**
 * Short label for the environment the app is actually running against, e.g.
 * 'local', 'dev', 'stage', 'prod'. Derived from the runtime project ID rather
 * than the build mode — one build travels between environments, so the build
 * mode says nothing about where it ended up.
 */
export function getEnvironmentLabel(): string {
  if (ENV.isLocalhost) return 'local'

  return ENV.environmentName || 'unknown'
}

export const HOST = {
  functions: '',
  cloudinary: '',
  translations: '',
}

export const EMU = Object.freeze({
  isEmulatingFunctions,
  isEmulatingFirestore,
  isEmulatingStorage,
  isEmulatingPubsub,
})

export function initEnvironment(projectID: string): void {
  if (!isFirebaseProjectID(projectID)) {
    throw new Error(
      `Unknown Firebase project ID '${projectID}' — no environment config exists for it`
    )
  }

  const config = environmentConfigs[projectID]

  ENV.isProd = projectID === 'lab-services-prod'
  ENV.isDev = !ENV.isProd
  ENV.projectID = projectID
  ENV.environmentName = config.environmentName

  HOST.functions = isEmulatingFunctions
    ? LAB_CLOUD_URL.EMULATOR
    : config.cloudFunctionsHost
  HOST.cloudinary = config.cloudinaryHostURL
  HOST.translations = config.translationsCDNHost
}
